import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { SagaChapterRef } from '@mezha/api'
import { useSagaChapterQuery, useSagaQuery, useSagaReadMutation } from '../../api/endpoints/saga'
import { useCommand } from '../../lib/useCommand'
import { Emblem } from '../../ui/brand/Emblems'
import { Empty, Failure, Loading } from '../../ui/State'
import { cx } from '../../ui'

/** Inline *emphasis* of the chapter text; everything else is plain text. */
function inline(text: string): ReactNode[] {
  return text.split(/(\*[^*\n]+\*)/g).map((part, i) =>
    part.length > 2 && part.startsWith('*') && part.endsWith('*') ? <em key={i}>{part.slice(1, -1)}</em> : <Fragment key={i}>{part}</Fragment>)
}

/**
 * The chapter text is the Markdown of the content package `saga`: paragraphs,
 * > quotes (the lullaby), *emphasis* and --- breaks. It is rendered as React
 * elements, never as HTML.
 */
export function SagaText({ text }: { text: string }) {
  const blocks = text.replace(/\r\n/g, '\n').split(/\n{2,}/).map(b => b.trim()).filter(Boolean)
  return (
    <div className="saga__text">
      {blocks.map((b, i) => {
        if (/^-{3,}$/.test(b)) return <hr key={i} className="saga__break" />
        if (b.startsWith('>')) {
          const lines = b.split('\n').map(l => l.replace(/^>\s?/, ''))
          return <blockquote key={i}>{lines.map((l, j) => <p key={j}>{inline(l)}</p>)}</blockquote>
        }
        return <p key={i}>{inline(b.replace(/\n/g, ' '))}</p>
      })}
    </div>
  )
}

type Group = { part: string; chapters: SagaChapterRef[] }

/** Journal tab «Повесть»: the novella opens chapter by chapter as quest rewards. */
export function SagaTab() {
  const saga = useSagaQuery()
  const chapters = useMemo(() => saga.data?.chapters ?? [], [saga.data])
  const opened = chapters.filter(c => c.unlocked)
  const [picked, setPicked] = useState<string | null>(null)
  const current = opened.find(c => c.id === picked) ?? opened.find(c => c.new) ?? opened[opened.length - 1] ?? null
  useEffect(() => { if (picked && !opened.some(c => c.id === picked)) setPicked(null) }, [picked, opened])


  if (saga.isLoading) return <Loading />
  if (saga.isError && !saga.data) return <Failure error={saga.error} retry={() => void saga.refetch()} what="Повесть" />
  if (!opened.length) {
    return <Empty>Повесть «Межа» открывается главами за пройденные задания. Первая глава — награда за первое задание на площади.</Empty>
  }

  const groups: Group[] = []
  for (const c of chapters) {
    const part = c.part || (c.order === 1 ? 'Пролог' : 'Эпилог')
    const last = groups[groups.length - 1]
    if (last && last.part === part) last.chapters.push(c)
    else groups.push({ part, chapters: [c] })
  }

  return (
    <>
      <nav className="road saga__road" aria-label="Главы повести">
        <p className="saga__lead">Повесть о Ждане. Её главы открываются наградой за пройденные задания: это история, которая идёт рядом с вашей.</p>
        {groups.map(g => {
          const shown = g.chapters.filter(c => c.unlocked)
          const closed = g.chapters.length - shown.length
          return (
            <div key={g.part}>
              <div className="road__act"><div className="plank"><div className="plank__in"><Emblem name="reward.saga" size={16} />{g.part}</div></div></div>
              <ol className="road__list">
                {shown.map(c => (
                  <li key={c.id}>
                    <button type="button" className={cx('rnode', !c.new && 'is-done', c.id === current?.id && 'is-current')} aria-current={c.id === current?.id ? 'true' : undefined}
                      onClick={() => setPicked(c.id)}>
                      <span className="rnode__mark" aria-hidden="true" />
                      <span>{c.title}<small>{c.new ? 'новая глава' : 'прочитано'}</small></span>
                    </button>
                  </li>
                ))}
                {closed > 0 && (
                  <li className="rnode is-locked"><span className="rnode__mark" aria-hidden="true" />
                    <span>{shown.length ? `Ещё ${closed} ${plural(closed)} — дальше по пути` : `${closed} ${plural(closed)} — откроются в следующих актах`}</span>
                  </li>
                )}
              </ol>
            </div>
          )
        })}
      </nav>
      {current?.id && <ChapterPage id={current.id} onGo={setPicked} />}
    </>
  )
}

function plural(n: number): string {
  const d = n % 10
  const t = n % 100
  if (d === 1 && t !== 11) return 'глава'
  if (d >= 2 && d <= 4 && (t < 12 || t > 14)) return 'главы'
  return 'глав'
}

function ChapterPage({ id, onGo }: { id: string; onGo: (id: string) => void }) {
  const ch = useSagaChapterQuery(id)
  const [read] = useSagaReadMutation()
  const cmd = useCommand()
  const c = ch.data
  const run = cmd.run
  const marked = useRef(new Set<string>())
  // Opening a new chapter marks it read once (a command with its own request_id).
  useEffect(() => {
    if (!c || c.read || marked.current.has(c.id)) return
    marked.current.add(c.id)
    void run(() => read({ chapter_id: c.id }).unwrap())
  }, [c, run, read])

  if (ch.isLoading) return <article className="qpage parch saga__page"><Loading /></article>
  if (ch.isError || !c) return <article className="qpage parch saga__page"><Failure error={ch.error} retry={() => void ch.refetch()} what="Глава" /></article>
  return (
    <article className="qpage parch saga__page" aria-labelledby="saga-title">
      <div className="qpage__code">{c.part || 'Повесть «Межа»'}</div>
      <h2 id="saga-title">{c.title}</h2>
      {c.place && <p className="qpage__intro saga__place">{c.place}</p>}
      <SagaText text={c.text} />
      <nav className="saga__turn" aria-label="Листать повесть">
        {c.prev_id ? <button type="button" className="go" onClick={() => onGo(c.prev_id!)}>◂ Предыдущая глава</button> : <span />}
        {c.next_id ? <button type="button" className="go" onClick={() => onGo(c.next_id!)}>Следующая глава ▸</button> : <span className="note">Следующая глава откроется дальше по пути</span>}
      </nav>
      {cmd.text && <p className="journal__error" role="alert">{cmd.text}</p>}
    </article>
  )
}
